import React, { Fragment } from "react";
import { observer } from "mobx-react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  LinearProgress,
  List,
  ListItem,
  MenuItem,
  Select,
  withStyles
} from "@material-ui/core";
import locale from "@bonliva-crm/locale";
import PrimaryButton from "../../common/buttons/PrimaryButton";
import TextField from "@material-ui/core/TextField/TextField";
import { SharedFileEditStore } from "./SharedFileEditStore";
import { Classes, ThemeType } from "@theme";
import Body from "../../common/typography/Body";
import Title from "../../common/typography/Title";
import RegularButton from "../../common/buttons/RegularButton";
import ContentSaveIcon from "react-icons/lib/md/save";
import filesize from "filesize";

export type EditableFile = {
  url: string;
  name: string;
  size: number;
  id: string;
  expirationDate: Date;
};

type Props = {
  classes: Classes<typeof styles>;
  store: SharedFileEditStore;
};

@observer
class SharedFilesEditDialog extends React.Component<Props> {
  handleChange = (index: number) => (event) => {
    const { store } = this.props;
    store.setName(index, event.target.value);
  };

  renderUploading = () => {
    const { classes, store } = this.props;
    const { editStatus } = store;

    if (!store.isEditing) {
      return null;
    }

    if (editStatus === "editing") {
      return (
        <div>
          <Body color="textSecondary" style={{ textAlign: "center" }}>
            {locale.Updating}
          </Body>

          <LinearProgress color="primary" />
        </div>
      );
    }

    if (editStatus === "success") {
      return (
        <div className={classes.result}>
          <Title>{locale.FileUploadSucceeded}</Title>
          <PrimaryButton onClick={store.close}>{locale.OK}</PrimaryButton>
        </div>
      );
    }

    /**
     * @todo: add "retry" functionality
     * @todo: add error message?
     */
    if (editStatus === "fail") {
      return (
        <div className={classes.result}>
          <Title>{locale.FileUpdateFail}</Title>

          <PrimaryButton onClick={store.close}>{locale.Cancel}</PrimaryButton>
        </div>
      );
    }

    return null;
  };

  renderContent = () => {
    const { store, classes } = this.props;

    return (
      <Fragment>
        <List>
          {store.files.map((file, idx) => (
            <ListItem key={idx} className={classes.file}>
              <TextField
                fullWidth
                className={classes.fileNameInput}
                label={locale.FileName}
                name={"name" + idx}
                value={store.files[idx].name}
                onChange={this.handleChange(idx)}
              />
              <div>
                <Body style={{ textAlign: "center" }}>({filesize(file.size)})</Body>
              </div>
            </ListItem>
          ))}
        </List>

        <div className={classes.expirationDate}>
          <FormControl>
            <InputLabel shrink htmlFor="expirationDate">
              {locale["Expiration date"]}
            </InputLabel>
            <Select
              className={classes.expirationSelect}
              id="expirationDate"
              autoWidth
              displayEmpty
              onChange={(e, v) => store.setExpirationDate(e.target.value as string)}
              value={store.expirationDate}
              data-test="ExpirationDateSelect"
            >
              <MenuItem value="" disabled>
                {locale["Select to renew"]}
              </MenuItem>
              <MenuItem value="7">1 {locale.Week.toLocaleLowerCase()}</MenuItem>
              <MenuItem value="31">1 {locale.Month.toLocaleLowerCase()}</MenuItem>
              <MenuItem value="365">1 {locale.Year.toLocaleString()}</MenuItem>
            </Select>
          </FormControl>
        </div>
      </Fragment>
    );
  };

  render() {
    const { store } = this.props;

    return (
      <Dialog
        open={store.isOpen}
        fullWidth
        maxWidth="sm"
        TransitionProps={{
          mountOnEnter: true,
          unmountOnExit: true
        }}
        onBackdropClick={store.close}
        id="filesUploadedDialog"
      >
        <DialogTitle>{locale.EditFile}</DialogTitle>
        <DialogContent>
          {store.isEditing ? this.renderUploading() : this.renderContent()}
        </DialogContent>

        <DialogActions>
          <RegularButton onClick={store.close}>{locale.Cancel}</RegularButton>
          <PrimaryButton
            disabled={!store.canSubmit}
            leftIcon={<ContentSaveIcon />}
            onClick={store.submit}
          >
            {locale.Save}
          </PrimaryButton>
        </DialogActions>
      </Dialog>
    );
  }
}
const styles = (theme: ThemeType) => ({
  file: {
    display: "flex",
    justifyContent: "center",
    padding: 0,
    flexDirection: "column",
    alignItems: "center"
  },

  fileNameInput: {
    marginBottom: 5
  },

  expirationDate: {
    justifyContent: "center",
    display: "flex",
    paddingTop: theme.spacing(),
    paddingBottom: theme.spacing(),
    whiteSpace: "nowrap"
  },

  expirationSelect: {
    minWidth: 240
  },

  result: {
    alignItems: "center",
    display: "flex",
    flexDirection: "column",

    "& > *:first-child": {
      marginBottom: theme.spacing(2)
    }
  }
});

export default withStyles(styles as any)(SharedFilesEditDialog);
